import styles from './Step.module.css';

import classNames from 'classnames';

import { useMutation } from '@tanstack/react-query';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';

import { AuthApi } from '../../api';
import { normalizePhoneNumber } from '../../utils';

interface RegistrationFormFields {
  surname: string;
  name: string;
  midname: string;
  username: string;
  password: string;
  confirmPassword: string;
  consent: boolean;
}

interface Step0Props {
  onNext: (data: { userId: number }) => void;
}

export const Step0: FC<Step0Props> = ({ onNext }) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RegistrationFormFields>();

  const { mutate } = useMutation({
    mutationFn: AuthApi.leadClientRegister,
    onSuccess: (response) => {
      const userId = response.data.userId;
      onNext({ userId });
    },
    onError: (err) => {
      console.error('Ошибка регистрации:', err);
    },
  });

  const onSubmit: SubmitHandler<RegistrationFormFields> = async (formData) => {
    const { consent, ...requestData } = formData;
    const normalizedPhone = normalizePhoneNumber(requestData.username);
    mutate({ ...requestData, username: normalizedPhone });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={styles.gridStep0Container}
    >
      <div className={classNames(styles.column, 'text-brand-1  rounded-l-lg')}>
        <header className="rounded-tl-lg bg-brand-6 px-[24px] py-[16px]">
          <h2 className="text-[20px] font-bold leading-normal">
            Личные данные
          </h2>
        </header>
        <div className="space-y-[20px] p-[20px]">
          <input
            {...register('surname', { required: true })}
            className={classNames(
              'input w-full',
              errors.surname && 'input--invalid',
            )}
            placeholder="Фамилия"
            type="text"
          />
          {errors.surname && (
            <p className="text-red-500 mt-[10px] text-xs">Обязательное поле</p>
          )}

          <input
            {...register('name', { required: true })}
            className={classNames(
              'input w-full',
              errors.name && 'input--invalid',
            )}
            placeholder="Имя"
            type="text"
          />
          {errors.name && (
            <p className="text-red-500 mt-[10px] text-xs">Обязательное поле</p>
          )}

          <input
            {...register('midname', { required: true })}
            className={classNames(
              'input w-full',
              errors.midname && 'input--invalid',
            )}
            placeholder="Отчество"
            type="text"
          />
          {errors.midname && (
            <p className="text-red-500 mt-[10px] text-xs">Обязательное поле</p>
          )}
          <Controller
            control={control}
            name="username"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <InputMask
                {...field}
                alwaysShowMask
                className={classNames(
                  'input w-full',
                  fieldState.invalid && 'input--invalid',
                )}
                mask="+7 999 999 99 99"
                maskPlaceholder={null}
                placeholder="Номер телефона"
                type="tel"
                value={field.value ?? ''}
              />
            )}
          />
          {errors.username && (
            <p className="text-red-500 mt-[10px] text-xs">Обязательное поле</p>
          )}
        </div>
      </div>

      <div className={classNames(styles.column, 'rounded-r-lg text-brand-1')}>
        <header className="rounded-tr-lg bg-brand-6 px-[24px] py-[16px]">
          <h2 className="text-[20px] font-bold leading-normal">Пароль</h2>
        </header>
        <div className="grow space-y-[20px] p-[20px]">
          <div>
            <input
              {...register('password', { required: true })}
              className={classNames(
                'input w-full',
                errors.password && 'input--invalid',
              )}
              placeholder="Пароль"
              type="password"
            />
            {errors.password && (
              <p className="text-red-500 mt-[10px] text-xs">
                Обязательное поле
              </p>
            )}
          </div>

          <div>
            <input
              {...register('confirmPassword', {
                required: true,
                validate: (value, formValues) =>
                  value === formValues.password || 'Пароли не совпадают',
              })}
              className={classNames(
                'input w-full',
                errors.confirmPassword && 'input--invalid',
              )}
              placeholder="Повторите пароль"
              type="password"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 mt-[10px] text-xs">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <div>
            <label className="flex items-center gap-[20px]">
              <input
                {...register('consent', { required: 'Необходимо согласие' })}
                type="checkbox"
                className={styles.checkbox}
              />
              <span>
                Я согласен с{' '}
                <a
                  href="https://my-pax.ru/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-5 underline"
                >
                  обработкой персональных данных
                </a>
              </span>
            </label>
            {errors.consent && (
              <p className="text-red-500 mt-[10px] text-xs">
                {errors.consent.message}
              </p>
            )}
          </div>
        </div>
        <footer className="mt-[150px] flex justify-end border-t border-dashed border-brand-1/50 p-[24px]">
          <button
            className="button button--primary flex w-full items-center justify-between px-[20px] text-left"
            type="submit"
          >
            Далее
            <span className="ml-[20px]">→</span>
          </button>
        </footer>
      </div>
    </form>
  );
};
